import { useState, useEffect, useCallback } from 'react';
import { type Event, type User, EventStatus, EVENT_STATUS_LABELS, isAdmin } from '@peles/shared';
import { useEvent, useUpdateEvent, useCancelEvent, useEventTypes } from '../../hooks/useEvents';
import { useVenues } from '../../hooks/useVenues';
import { useDialog } from '../../context/DialogContext';

interface EventDetailPanelProps {
  eventId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
}

interface FormState {
  name: string;
  eventTypeId: string;
  /** datetime-local string: "YYYY-MM-DDTHH:mm" in browser-local time. */
  startAt: string;
  /** datetime-local string. Empty when the event has no defined end. */
  endAt: string;
  venueId: string;
  guestCount: string;
  budget: string;
  status: EventStatus;
  notes: string;
}

/** Convert a Date (UTC stored) to a "YYYY-MM-DDTHH:mm" string in browser-local time. */
function toLocalInputValue(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = new Date(d);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function eventToFormState(event: Event): FormState {
  return {
    name: event.name,
    eventTypeId: event.eventTypeId,
    startAt: toLocalInputValue(event.startAt),
    endAt: toLocalInputValue(event.endAt),
    venueId: event.venueId || '',
    guestCount: event.guestCount?.toString() || '',
    budget: event.budget?.toString() || '',
    status: event.status,
    notes: event.notes || '',
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (
    form.name !== original.name ||
    form.eventTypeId !== original.eventTypeId ||
    form.startAt !== original.startAt ||
    form.endAt !== original.endAt ||
    form.venueId !== original.venueId ||
    form.guestCount !== original.guestCount ||
    form.budget !== original.budget ||
    form.status !== original.status ||
    form.notes !== original.notes
  );
}

export function EventDetailPanel({
  eventId,
  onClose,
  onUnsavedChange,
  currentUser,
}: EventDetailPanelProps) {
  const { confirm } = useDialog();
  const { data: event, isLoading } = useEvent(eventId);
  const updateEvent = useUpdateEvent();
  const cancelEvent = useCancelEvent();
  const { data: eventTypes = [] } = useEventTypes();
  const { data: venues = [] } = useVenues({ isActive: true });

  const [form, setForm] = useState<FormState>({
    name: '',
    eventTypeId: '',
    startAt: '',
    endAt: '',
    venueId: '',
    guestCount: '',
    budget: '',
    status: EventStatus.DRAFT,
    notes: '',
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [saved, setSaved] = useState(false);

  const currentUserIsAdmin = isAdmin(currentUser.systemRole);

  // Initialize form when event data loads or eventId changes
  useEffect(() => {
    if (event) {
      const state = eventToFormState(event);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [event]);

  // Track dirty state
  const dirty = isDirty(form, originalForm);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (form.name !== originalForm.name) updates.name = form.name;
    if (form.eventTypeId !== originalForm.eventTypeId) updates.eventTypeId = form.eventTypeId;
    if (form.startAt !== originalForm.startAt) {
      updates.startAt = new Date(form.startAt);
    }
    if (form.endAt !== originalForm.endAt) {
      updates.endAt = form.endAt ? new Date(form.endAt) : null;
    }
    if (form.venueId !== originalForm.venueId) updates.venueId = form.venueId || null;
    if (form.guestCount !== originalForm.guestCount) {
      updates.guestCount = form.guestCount ? parseInt(form.guestCount, 10) : null;
    }
    if (form.budget !== originalForm.budget) {
      updates.budget = form.budget ? parseFloat(form.budget) : null;
    }
    if (form.status !== originalForm.status) updates.status = form.status;
    if (form.notes !== originalForm.notes) updates.notes = form.notes || null;

    updateEvent.mutate(
      { id: eventId, updates },
      {
        onSuccess: (updated) => {
          const state = eventToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, eventId, updateEvent]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCancelEvent = useCallback(async () => {
    if (
      await confirm({
        title: 'Cancel Event',
        message: `Cancel event "${event?.name}"? This action cannot be undone.`,
        confirmLabel: 'Cancel Event',
        cancelLabel: 'Keep Event',
      })
    ) {
      cancelEvent.mutate(eventId);
    }
  }, [eventId, event?.name, cancelEvent, confirm]);

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Event not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-semibold flex-1">Event Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="detail-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="detail-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="detail-event-type"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Event Type
          </label>
          <select
            id="detail-event-type"
            value={form.eventTypeId}
            onChange={(e) => setForm((f) => ({ ...f, eventTypeId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {eventTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="detail-start-at" className="block text-sm font-medium text-gray-700 mb-1">
            Start
          </label>
          <input
            id="detail-start-at"
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="detail-end-at" className="block text-sm font-medium text-gray-700 mb-1">
            End <span className="text-xs text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="detail-end-at"
            type="datetime-local"
            value={form.endAt}
            onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
            min={form.startAt || undefined}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="detail-venue" className="block text-sm font-medium text-gray-700 mb-1">
            Venue
          </label>
          <select
            id="detail-venue"
            value={form.venueId}
            onChange={(e) => setForm((f) => ({ ...f, venueId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">No venue</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
                {venue.city ? ` — ${venue.city}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="detail-guest-count"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Guest Count
          </label>
          <input
            id="detail-guest-count"
            type="number"
            value={form.guestCount}
            onChange={(e) => setForm((f) => ({ ...f, guestCount: e.target.value }))}
            placeholder="Enter guest count"
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="detail-budget" className="block text-sm font-medium text-gray-700 mb-1">
            Budget
          </label>
          <input
            id="detail-budget"
            type="number"
            value={form.budget}
            onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
            placeholder="Enter budget"
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="detail-status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="detail-status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as EventStatus }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.values(EventStatus).map((status) => (
              <option key={status} value={status}>
                {EVENT_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="detail-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="detail-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Enter notes"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
            {new Date(event.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || updateEvent.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateEvent.isPending ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {/* Danger Zone */}
      {currentUserIsAdmin && event.status !== EventStatus.CANCELLED && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
          <button
            onClick={handleCancelEvent}
            disabled={cancelEvent.isPending}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {cancelEvent.isPending ? 'Cancelling...' : 'Cancel Event'}
          </button>
        </div>
      )}
    </div>
  );
}
